import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, ArrowRight } from 'lucide-react';
import { PITIComparison, formatCurrency, formatPercentage } from '@/utils/PITIComparison';

interface PITIComparisonCardProps {
  comparison: PITIComparison;
  title: string;
  description?: string;
  showLifetimeSavings?: boolean;
  className?: string;
}

export const PITIComparisonCard: React.FC<PITIComparisonCardProps> = ({
  comparison,
  title,
  description,
  showLifetimeSavings = true,
  className = ""
}) => {
  const { current, improved, savings } = comparison;
  const isImprovement = savings.monthly > 0;

  return (
    <Card className={`bg-slate-900/80 border border-slate-700 ${className}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-slate-200 text-lg flex items-center gap-2">
          {isImprovement ? (
            <TrendingUp className="w-5 h-5 text-green-400" />
          ) : (
            <TrendingDown className="w-5 h-5 text-red-400" />
          )}
          {title}
        </CardTitle>
        {description && (
          <p className="text-sm text-slate-400">{description}</p>
        )}
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Scenario Headers */}
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <h4 className="font-semibold text-slate-300 mb-1">{current.scenario}</h4>
            <Badge variant="outline" className="text-xs">
              {current.rate.toFixed(2)}% APR
            </Badge>
          </div>
          <div className="text-center">
            <h4 className="font-semibold text-slate-300 mb-1">{improved.scenario}</h4>
            <Badge variant="outline" className="text-xs">
              {improved.rate.toFixed(2)}% APR
            </Badge>
          </div>
        </div>

        {/* PITI Breakdown Comparison */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-400">Principal & Interest:</span>
              <span className="text-slate-200">
                {formatCurrency(current.breakdown.principal + current.breakdown.interest)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Property Taxes:</span>
              <span className="text-slate-200">{formatCurrency(current.breakdown.taxes)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Insurance:</span>
              <span className="text-slate-200">{formatCurrency(current.breakdown.insurance)}</span>
            </div>
            {current.breakdown.pmi > 0 && (
              <div className="flex justify-between">
                <span className="text-slate-400">PMI:</span>
                <span className="text-slate-200">{formatCurrency(current.breakdown.pmi)}</span>
              </div>
            )}
            {current.breakdown.hoa > 0 && (
              <div className="flex justify-between">
                <span className="text-slate-400">HOA:</span>
                <span className="text-slate-200">{formatCurrency(current.breakdown.hoa)}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-slate-600 pt-2 font-semibold">
              <span className="text-slate-200">Total PITI:</span>
              <span className="text-slate-200">{formatCurrency(current.breakdown.total)}</span>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-400">Principal & Interest:</span>
              <span className="text-slate-200">
                {formatCurrency(improved.breakdown.principal + improved.breakdown.interest)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Property Taxes:</span>
              <span className="text-slate-200">{formatCurrency(improved.breakdown.taxes)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Insurance:</span>
              <span className="text-slate-200">{formatCurrency(improved.breakdown.insurance)}</span>
            </div>
            {improved.breakdown.pmi > 0 && (
              <div className="flex justify-between">
                <span className="text-slate-400">PMI:</span>
                <span className="text-slate-200">{formatCurrency(improved.breakdown.pmi)}</span>
              </div>
            )}
            {improved.breakdown.hoa > 0 && (
              <div className="flex justify-between">
                <span className="text-slate-400">HOA:</span>
                <span className="text-slate-200">{formatCurrency(improved.breakdown.hoa)}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-slate-600 pt-2 font-semibold">
              <span className="text-slate-200">Total PITI:</span>
              <span className="text-slate-200">{formatCurrency(improved.breakdown.total)}</span>
            </div>
          </div>
        </div>

        {/* Savings Summary */}
        <div className="bg-slate-800/50 border border-slate-600 rounded-lg p-4">
          <div className="flex items-center justify-center gap-2 mb-3">
            <ArrowRight className="w-4 h-4 text-blue-400" />
            <span className="font-semibold text-slate-200">Impact Analysis</span>
          </div>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="text-center">
              <div className="text-slate-400 mb-1">Monthly</div>
              <div className={`font-semibold ${isImprovement ? 'text-green-400' : 'text-red-400'}`}>
                {isImprovement ? '+' : ''}{formatCurrency(Math.abs(savings.monthly))}
              </div>
              <div className="text-xs text-slate-500">
                {formatPercentage(savings.percentImprovement)} {isImprovement ? 'savings' : 'increase'}
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-slate-400 mb-1">Annual</div>
              <div className={`font-semibold ${isImprovement ? 'text-green-400' : 'text-red-400'}`}>
                {isImprovement ? '+' : ''}{formatCurrency(Math.abs(savings.annual))}
              </div>
            </div>
          </div>

          {showLifetimeSavings && (
            <div className="mt-3 pt-3 border-t border-slate-600 text-center">
              <div className="text-slate-400 mb-1">Total Lifetime Impact</div>
              <div className={`text-lg font-bold ${isImprovement ? 'text-green-400' : 'text-red-400'}`}>
                {isImprovement ? '+' : ''}{formatCurrency(Math.abs(savings.lifetime))}
              </div>
              <div className="text-xs text-slate-500">
                Over 30-year loan term
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};